import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import VerificationCodeInput from "@/components/Input/verifyCodeInput";
import { Toast } from "@/components/Toast";
import LoginStore, { loginStore } from "@/modules/login/store";
import { getEmailVCode, getPhoneVCode } from "@/modules/login/api";
import { observer } from "mobx-react-lite";
import { Box, Button, Flex, Image, Input, Text } from "@chakra-ui/react";
import Icon from "@/components/Icon";
import { themeStore } from "@/stores/theme";
import { isValidEmail, isValidPhone } from "@/utils/string";
import { langStore, translate } from "@noli/business";
import { Login_Words, Toast_Words } from "@/utils/lang";
import uiConfig from "@/components/config";
import { useEventListener } from "ahooks";
import { useOwnToast } from "@/utils/misc";

const SMSLogin = () => {
  const { phone, visible } = loginStore.loginState;
  const { loginBtnColor, loginBtnDisableColor, loginBtnTextColor } = themeStore.styles;
  const [account, setAccount] = useState("");
  const [vCode, setVCode] = useState("");
  const [loading, setLoading] = useState(false);
  const toast = useOwnToast();
  const [prevGetVCodeTimestamp] = useState<string | null>(
    localStorage.getItem("prevGetVCodeTimestamp"),
  );

  useEventListener("keydown", (ev) => {
    if (loginStore.dialogType !== "phoneCode") return;
    if (ev.code === "Enter") {
      if (!loginEnable) {
        toast({
          description: "手机号或验证码格式不正确",
          status: "warning",
          duration: 1500,
          position: "top",
        });
      } else {
        handlerLogin();
      }
    }
  });

  const lang = langStore.tgt;
  const getVerifyTranslate = useCallback(
    (time: number) => {
      return time > 0
        ? translate(Login_Words.codeDisable, lang).replace("$", String(time))
        : translate(Login_Words.getCode, lang);
    },
    [lang],
  );

  const accountVerification = useMemo(
    (): boolean => (lang === "zh" ? isValidPhone(account) : isValidEmail(account)),
    [account, lang],
  );

  const vCodeVerification = useMemo((): boolean => {
    const reg = /^[0-9]\d{5}$/;
    return reg.test(vCode);
  }, [vCode]);

  const loginEnable = useMemo(
    () => accountVerification && vCodeVerification,
    [accountVerification, vCodeVerification],
  );

  const handlerPhoneChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const account = e.target.value;
    if (isValidEmail(account)) {
      loginStore?.setEmail(account);
    }
    if (isValidPhone(account)) {
      loginStore?.setPhone(account);
    }
    setAccount(account);
  };

  const handlerVCodeChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const code = e.target.value;
    setVCode(code);
  };

  const handlerGetVCode = useCallback(() => {
    if (accountVerification) {
      localStorage.setItem("prevGetVCodeTimestamp", `${new Date().getTime()}`);
      if (isValidPhone(account)) {
        getPhoneVCode({
          mobileNumber: account,
        }).catch((err: any) => {
          Toast({
            title: translate(Toast_Words.codeSendFail, lang),
            status: "error",
          });
        });
      }
      // else {
      //   getEmailVCode({ email: account  }).catch((err: any) => {
      //     Toast({
      //       title: "验证码发送失败",
      //       status: "error",
      //     });
      //   });
      // }
    } else {
      Toast({
        title: translate(Toast_Words.accountInvalid, lang),
        status: "warning",
      });
    }

    return accountVerification;
  }, [accountVerification, account, lang]);

  const handlerLogin = () => {
    setLoading(true);
    loginStore
      ?.loginByVerificationCode({
        mobileNumber: isValidPhone(account) ? account : "",
        email: isValidEmail(account) ? account : "",
        code: vCode,
        channel_code: sessionStorage.getItem("utm") ?? "",
      })
      .then(async (res) => {
        const { is_new } = res;
        if (res.errCode == 0) {
          Toast({
            title: translate(Toast_Words.loginSuccess, lang),
            status: "success",
          });
          loginStore?.closeLoginDialog();
        } else {
          Toast({
            title: translate(Toast_Words.loginFailed, lang),
            status: "error",
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (visible) setVCode("");
  }, [visible]);

  return (
    <Box w="100%">
      <Text mt="16px" mb="8px" fontSize="14px" color={"#4E5969"}>
        手机号
      </Text>
      <Input
        borderColor="fill.emphasis"
        _focusVisible={{ borderColor: loginBtnColor }}
        value={account}
        color="inherit"
        _placeholder={{ color: "#C9CDD4" }}
        fontSize={uiConfig.inputFontSize}
        onChange={handlerPhoneChange}
        placeholder="请输入手机号"
      />
      <Text mt="16px" mb="8px" fontSize="14px" color={"#4E5969"}>
        验证码
      </Text>
      <VerificationCodeInput
        btnText={getVerifyTranslate}
        value={vCode}
        onChange={handlerVCodeChange}
        length={6}
        placeholder="请输入验证码"
        onGetCode={handlerGetVCode}
        prevGetCodeTimestamp={prevGetVCodeTimestamp}
      />
      <Button
        w="100%"
        rounded="full"
        disabled={!loginEnable}
        mt="40px"
        bgColor={loginBtnColor}
        _disabled={{
          bgColor: loginBtnDisableColor,
        }}
        _hover={{}}
        _focus={{}}
        _active={{}}
        color={loginBtnTextColor}
        onClick={handlerLogin}
        loadingText={"登录中..."}
        isLoading={loading}
        style={{ fontWeight: 600, marginBottom: "21px", height: "40px" }}
      >
        登录
      </Button>
    </Box>
  );
};

export default observer(SMSLogin);
